// components/ShippingEditModal.jsx
import React, { useState, useEffect } from "react";
import { updateSampleRequest, invokeSampleShippedEmail, loadShippingCarrier } from "../services/posService";
import ShippingLabelModal from "./ShippingLabelModal";
import { shipStatusOptions, shipBlockedMessage } from "../utils/approval.js";
import "./ShippingEditModal.css";

const STATUS_OPTIONS = ["Not Shipped", "In Process", "ON HOLD", "Shipped"];

const CARRIER_OPTIONS = ["", "FedEx", "UPS", "USPS", "Other"];

/**
 * `approvalBlocked` (samples only, migration 0097): the request came through the
 * public sample form and has not been approved, so the database will refuse to
 * let it reach 'Shipped'. When set, this modal drops the Shipped option and the
 * Create-UPS-Label button entirely — the label button matters most, because it
 * ticks "mark shipped" by default and the label is bought from UPS *before* the
 * status write, so hitting the trigger there would cost real postage on a
 * request that then cannot ship. The reviewer gets a line saying why and a way
 * through to the approval modal instead.
 */
export default function ShippingEditModal({ request, onClose, onSaved, user, onNotifyResult, approvalBlocked = false, onReview }) {
  const [status,      setStatus]      = useState(request.status      ?? "Not Shipped");
  const [shipDate,    setShipDate]    = useState(request.shipDate     ?? "");
  const [estDelivery, setEstDelivery] = useState(request.estDelivery  ?? "");
  const [carrier,     setCarrier]     = useState(request.carrier      ?? "");
  const [tracking,    setTracking]    = useState(request.tracking     ?? "");
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);
  const [labelEnabled, setLabelEnabled] = useState(false);
  const [showLabel,   setShowLabel]   = useState(false);

  useEffect(() => {
    loadShippingCarrier("ups")
      .then(c => setLabelEnabled(!!(c?.enabled && c?.has_credentials)))
      .catch(() => {});
  }, []);

  // Label created server-side (carrier/tracking/ship_date already stamped on the
  // request). Reflect it here and bubble the updated row to the grid.
  const handleLabeled = (fields) => {
    if (fields.carrier !== undefined) setCarrier(fields.carrier);
    if (fields.tracking !== undefined) setTracking(fields.tracking);
    if (fields.shipDate) setShipDate(fields.shipDate);
    if (fields.status) setStatus(fields.status);
    onSaved({ ...request, ...fields, section: fields.status === "Shipped" ? "shipped" : request.section });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const wasShipped = request.status === "Shipped";
      await updateSampleRequest(request.id, {
        ...request,
        status,
        shipDate:    shipDate    || null,
        estDelivery: estDelivery || null,
        carrier:     carrier     || null,
        tracking:    tracking    || null,
      });
      // Notify submitter if transitioning to Shipped. Item 204: the result is
      // reported up so the page can say the notice did not go out.
      if (status === "Shipped" && !wasShipped) {
        invokeSampleShippedEmail(
          request,
          { shipDate: shipDate || null, estDelivery: estDelivery || null, carrier: carrier || null, tracking: tracking || null },
          user?.mail
        )
          .then(r => onNotifyResult?.(r))
          .catch(err => { console.error(err); onNotifyResult?.({ sent: false, reason: "invoke_failed" }); });
      }
      onSaved({
        ...request,
        status,
        shipDate,
        estDelivery,
        carrier,
        tracking,
        section: deriveSection(status),
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sem-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sem-modal">

        <div className="sem-header">
          <div className="sem-title">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
            Edit Shipping Details
          </div>
          <button className="sem-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="sem-subheader">
          {request.shipToCompany && <span className="sem-company">{request.shipToCompany}</span>}
          {request.contactName   && <span className="sem-contact">{request.contactName}</span>}
        </div>

        <div className="sem-body">

          {approvalBlocked && (
            <div className="sem-gate" role="status">
              <span>{shipBlockedMessage(request)}</span>
              {onReview && (
                <button type="button" className="sem-gate-btn" onClick={onReview}>
                  Review approval
                </button>
              )}
            </div>
          )}

          <div className="sem-row">
            <div className="sem-field sem-field-grow">
              <label>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}>
                {/* One predicate, same as the grid: a blocked row simply has no
                    Shipped option to pick. */}
                {shipStatusOptions(STATUS_OPTIONS, request).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="sem-row">
            <div className="sem-field">
              <label>Ship Date</label>
              <input type="date" value={shipDate} onChange={e => setShipDate(e.target.value)} />
            </div>
            <div className="sem-field">
              <label>Est. Delivery</label>
              <input type="date" value={estDelivery} onChange={e => setEstDelivery(e.target.value)} />
            </div>
          </div>

          <div className="sem-row">
            <div className="sem-field">
              <label>Carrier</label>
              <select value={carrier} onChange={e => setCarrier(e.target.value)}>
                {CARRIER_OPTIONS.map(c => <option key={c} value={c}>{c || "— Select —"}</option>)}
              </select>
            </div>
            <div className="sem-field sem-field-grow">
              <label>Tracking Number</label>
              <input
                type="text"
                value={tracking}
                onChange={e => setTracking(e.target.value)}
                placeholder="Enter tracking number"
              />
            </div>
          </div>

          {error && <div className="sem-error">{error}</div>}
        </div>

        <div className="sem-footer">
          <button className="sem-cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
          {labelEnabled && !approvalBlocked && (
            <button
              type="button"
              className="sem-cancel-btn"
              onClick={() => setShowLabel(true)}
            >
              Create UPS Label
            </button>
          )}
          <button className="sem-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>

      </div>

      {showLabel && (
        <ShippingLabelModal
          order={request}
          orderType="sample"
          onClose={() => setShowLabel(false)}
          onLabeled={handleLabeled}
        />
      )}
    </div>
  );
}

function deriveSection(status) {
  if (status === "Shipped") return "shipped";
  return "new";
}
